import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  ArrowLeft,
  BarChart3,
  PieChart as PieChartIcon,
  TrendingUp
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer
} from "recharts";
import { formatCurrency as formatCurrencyUtil } from '../utils/sharedCalculations';
import { usePlanningYears } from '../hooks/usePlanningYears';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const PerformanceAnalytics = () => {
  const navigate = useNavigate();
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  const [resourceAllocation, setResourceAllocation] = useState(null);
  const planningData = usePlanningYears(currentPlan?.id);
  const { years } = planningData;

  const formatCurrency = (amount, compact = false) => {
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  // Load performance data
  const loadPerformanceData = async () => {
    if (!currentPlan?.id || !years || years.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch multi-year financial data
      const multiYearPromises = years.map(year =>
        axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`)
          .then(res => {
            const income = res.data.financial_statements?.income_statement || {};
            const keyMetrics = res.data.financial_statements?.key_metrics || {};
            return {
              year,
              revenue: income.revenue || 0,
              grossProfit: income.gross_profit || 0,
              netIncome: income.net_income || 0,
              grossMargin: keyMetrics.gross_margin_percent || 0,
              netMargin: keyMetrics.net_margin_percent || 0
            };
          })
          .catch(() => ({ year, revenue: 0, grossProfit: 0, netIncome: 0, grossMargin: 0, netMargin: 0 }))
      );
      
      const multiYearData = await Promise.all(multiYearPromises);
      
      // Fetch cost data for resource allocation
      const costResponse = await axios.get(`${BACKEND_URL}/api/v3/cost-items/${currentPlan.id}`);
      const costData = costResponse.data || [];
      
      // Calculate resource allocation
      const allocation = {};
      let total = 0;
      
      costData.forEach(cost => {
        const category = cost.cost_category || 'Other';
        const amount = cost.amount || 0;
        allocation[category] = (allocation[category] || 0) + amount;
        total += amount;
      });
      
      const allocationArray = Object.entries(allocation).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: value,
        percentage: total > 0 ? (value / total) * 100 : 0
      }));
      
      setPerformanceData(multiYearData);
      setResourceAllocation({
        data: allocationArray,
        total: total
      });
      
    } catch (error) {
      console.error('Error loading performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPerformanceData();
  }, [currentPlan?.id, years]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/planning-dashboard')}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Planning Dashboard
        </Button>
        <TooltipLabel
          label={<h1 className="text-3xl font-bold text-slate-900">Performance Analytics</h1>}
          tooltip="Comprehensive performance analysis including revenue, profitability, and resource allocation"
          asChild
        />
        <p className="text-slate-600 mt-1">
          Revenue, profitability, and operational performance for {currentPlan?.company_name || currentPlan?.name}
        </p>
      </div>

      {/* Revenue & Profitability Chart */}
      {performanceData && performanceData.length > 0 && (
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                  Revenue & Profitability Trend
                </CardTitle>
              }
              tooltip="This chart shows 3 key metrics over 5 years: (1) Revenue (blue) - total income from all sources, (2) Gross Profit (green) - revenue minus cost of goods sold (COGS), shows profitability before operating expenses, (3) Net Income (purple) - final profit after all expenses. Example: $1M revenue, $700K gross profit (30% gross margin), $200K net income (20% net margin). Look for: consistent upward trends (growth), widening gap between revenue and net income (improving efficiency), or narrowing gaps (margin compression). All three should trend upward in healthy businesses."
              asChild
            />
            <CardDescription>5-year revenue, gross profit, and net income projection</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="year" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip formatter={(value) => formatCurrency(value, true)} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    name="Revenue"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="grossProfit" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    name="Gross Profit"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="netIncome" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    name="Net Income"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profit Margins Chart */}
      {performanceData && performanceData.length > 0 && (
        <Card className="border-t-4 border-t-green-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                  Profit Margins
                </CardTitle>
              }
              tooltip="Profit margins show what percentage of revenue becomes profit at different stages. (1) Gross Margin % (green bars) = (Revenue - COGS) ÷ Revenue × 100. Measures production/delivery efficiency. (2) Net Margin % (blue bars) = Net Profit ÷ Revenue × 100. Measures overall profitability after all expenses. Example: 60% gross margin means $60 profit from every $100 sale before operating costs; 20% net margin means $20 final profit after everything. Benchmarks: SaaS Gross 70-85%, Retail 30-50%; Net margins 10-20% considered healthy. Rising margins = improving efficiency; falling margins = increasing costs or pricing pressure."
              asChild
            />
            <CardDescription>Gross margin and net margin percentage trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="year" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                  <Legend />
                  <Bar dataKey="grossMargin" fill="#10b981" name="Gross Margin %" />
                  <Bar dataKey="netMargin" fill="#3b82f6" name="Net Margin %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resource Allocation */}
      {resourceAllocation && resourceAllocation.data && resourceAllocation.data.length > 0 && (
        <Card className="border-t-4 border-t-amber-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <PieChartIcon className="h-5 w-5 mr-2 text-amber-600" />
                  Resource Allocation
                </CardTitle>
              }
              tooltip="Resource Allocation shows how your budget is distributed across different cost categories. Each category represents a type of expense (Personnel, Marketing, Operations, Technology, etc.). Example: 40% Personnel ($400K), 25% Marketing ($250K), 20% Operations ($200K), 15% Tech ($150K) from $1M budget. Use this to: (1) Identify largest expense areas, (2) Compare with industry benchmarks (e.g., SaaS typically 30-40% on sales/marketing), (3) Spot imbalances (e.g., 60% on overhead might indicate inefficiency), (4) Plan budget adjustments. Click any row for detailed breakdown. Sorted by allocation % (largest first) for easy analysis."
              asChild
            />
            <CardDescription>Budget distribution by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Donut Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={resourceAllocation.data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={(entry) => `${entry.name}: ${entry.percentage.toFixed(1)}%`}
                    >
                      {resourceAllocation.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Enhanced Category Breakdown - Futuristic Table */}
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-slate-900">Total Budget</span>
                    <span className="text-2xl font-bold text-amber-600">{formatCurrency(resourceAllocation.total)}</span>
                  </div>
                </div>
                
                {/* Scrollable Table for Many Items */}
                <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase tracking-wider">Category</th>
                        <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase tracking-wider">Amount</th>
                        <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase tracking-wider">%</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-700 uppercase tracking-wider">Distribution</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {resourceAllocation.data
                        .sort((a, b) => b.percentage - a.percentage)
                        .map((item, index) => (
                          <tr 
                            key={index}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                            title={`${item.name}: ${formatCurrency(item.value)} (${item.percentage.toFixed(1)}% of total budget)`}
                          >
                            <td className="p-3">
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="w-3 h-3 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                ></div>
                                <span className="text-sm font-medium text-slate-900">{item.name}</span>
                              </div>
                            </td>
                            <td className="p-3 text-right">
                              <span className="text-sm font-semibold text-slate-900">
                                {formatCurrency(item.value, true)}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <span className="text-sm font-bold text-amber-600">
                                {item.percentage.toFixed(1)}%
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center space-x-2">
                                <div className="flex-1 bg-slate-200 rounded-full h-2">
                                  <div 
                                    className="h-2 rounded-full transition-all duration-500"
                                    style={{ 
                                      backgroundColor: COLORS[index % COLORS.length],
                                      width: `${item.percentage}%` 
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-slate-600">Categories</p>
                    <p className="text-lg font-bold text-blue-600">{resourceAllocation.data.length}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="text-xs text-slate-600">Largest</p>
                    <p className="text-lg font-bold text-green-600">
                      {resourceAllocation.data.length > 0 
                        ? Math.max(...resourceAllocation.data.map(d => d.percentage)).toFixed(1) + '%'
                        : '0%'}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <p className="text-xs text-slate-600">Smallest</p>
                    <p className="text-lg font-bold text-purple-600">
                      {resourceAllocation.data.length > 0 
                        ? Math.min(...resourceAllocation.data.map(d => d.percentage)).toFixed(1) + '%'
                        : '0%'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PerformanceAnalytics;
